import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Play, Shuffle, X, FolderOpen } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { usePlayer } from "../context/PlayerContext";
import { useMyFiles, addFiles, removeFile, emptyList, songOf, sizeText, MAX_FILES } from "../lib/myFiles";

// My Videos (Premium): the member picks video files on their own phone, tablet or computer and plays them in the big player,
// with the lights, Shuffle, Repeat and Autoplay like any playlist. Nothing is uploaded and nothing is saved on the site
// (lib/myFiles.js keeps the list in memory only). The player knows these as songs marked "local".
// A guest or a free member can pick a video too and try ONE for 10 seconds; then PlayerContext asks them to create a login
// or go Premium (up to 200 videos).
export default function MyVideos() {
  const { user } = useAuth();
  const p = usePlayer();
  const files = useMyFiles();
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const premium = !!user && (user.is_premium || user.role === "admin");
  const songs = useMemo(() => files.map(songOf), [files]);

  const take = (list) => {
    const r = addFiles(list);
    if (r.added) toast.success(`${r.added} file${r.added === 1 ? "" : "s"} added`);
    const left = [];
    if (r.notVideo) left.push(`${r.notVideo} ${r.notVideo === 1 ? "was" : "were"} not a video or music file`);
    if (r.same) left.push(`${r.same} ${r.same === 1 ? "was" : "were"} already in the list`);
    if (r.full) left.push(`${r.full} did not fit (the list holds ${MAX_FILES})`);
    if (left.length) toast(`Left out: ${left.join(", ")}.`);
  };
  const choose = () => { if (inputRef.current) inputRef.current.click(); };
  const onPick = (e) => { take(e.target.files); e.target.value = ""; };
  const onDrop = (e) => { e.preventDefault(); setOver(false); take(e.dataTransfer.files); };
  const playAt = (i) => p.playQueue(songs, i, premium ? { playlist: true } : undefined);
  const shuffleAll = () => {
    if (!songs.length) return;
    p.setShuffle(true);
    playAt(Math.floor(Math.random() * songs.length));
  };
  const nowId = p.current && p.current.local ? p.current.id : null;

  return (
    <div data-testid="myvideos-page">
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold mb-3">My Videos</h1>
      {premium ? (
        <p className="text-zinc-400 max-w-2xl mb-1" data-testid="myvideos-intro">Play MP4 videos and MP3 or WAV music from this device in the big player, with the lights, Shuffle, Repeat and Autoplay. The list holds up to {MAX_FILES} files. Music files show the Dan The Man TV astronaut.</p>
      ) : (
        <p className="text-zinc-400 max-w-2xl mb-1" data-testid="myvideos-intro-try">Try it: pick a video or a music file (MP4, MP3 or WAV) from this device and play it for 10 seconds. Premium members play up to {MAX_FILES} of their own files in full, with the lights, Shuffle, Repeat and Autoplay.{!user ? " Create a free login first, then go Premium." : ""}</p>
      )}
      <p className="text-zinc-500 text-sm max-w-2xl mb-6">Your files stay on your device. Nothing is uploaded to Dan The Man TV. The list is forgotten when you close or refresh this page, so pick them again next time. Standard MP4 videos and MP3 or WAV music play everywhere; some other kinds may not play in your browser.</p>

      <input ref={inputRef} type="file" accept="video/mp4,video/*,audio/*,.mp4,.m4v,.mov,.webm,.mp3,.wav,.m4a,.aac,.ogg,.flac" multiple className="hidden" onChange={onPick} data-testid="myvideos-input" />
      <div
        onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={onDrop}
        data-testid="myvideos-drop"
        className={`rounded-2xl border-2 border-dashed p-6 mb-6 flex flex-wrap items-center gap-3 ${over ? "border-cyan-400 bg-cyan-400/10" : "border-white/15 bg-zinc-900/40"}`}>
        <button type="button" onClick={choose} data-testid="myvideos-choose"
          className="px-4 py-2 rounded-full bg-cyan-400 text-black font-semibold hover:bg-cyan-300 flex items-center gap-2">
          <FolderOpen className="w-4 h-4" /> Choose videos or music from this device
        </button>
        <span className="text-sm text-zinc-500">{files.length ? `${files.length} in the list` : "or drop video or music files here"}</span>
      </div>

      {files.length > 0 && (
        <>
          <div className="flex flex-wrap gap-2 mb-4">
            <button type="button" onClick={() => playAt(0)} data-testid="myvideos-play-all"
              className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/10 flex items-center gap-2"><Play className="w-4 h-4" /> Play all</button>
            {premium && (
              <button type="button" onClick={shuffleAll} data-testid="myvideos-shuffle-all"
                className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/10 flex items-center gap-2"><Shuffle className="w-4 h-4" /> Shuffle all</button>
            )}
            <button type="button" onClick={emptyList} data-testid="myvideos-clear"
              className="px-4 py-2 rounded-full text-zinc-400 hover:text-white hover:bg-white/5 border border-white/10">Clear the list</button>
          </div>
          <ul className="space-y-2" data-testid="myvideos-list">
            {songs.map((s, i) => (
              <li key={s.id} data-testid={`myvideos-row-${i}`}
                className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${nowId === s.id ? "border-cyan-400/60 bg-cyan-400/5" : "border-white/10 bg-zinc-900/50"}`}>
                <button type="button" onClick={() => playAt(i)} aria-label={`Play ${s.title}`} data-testid={`myvideos-play-${i}`}
                  className="steel-btn w-9 h-9 shrink-0 flex items-center justify-center"><Play className="w-4 h-4" /></button>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{s.title}</div>
                  <div className="text-xs text-zinc-500">{nowId === s.id ? "Playing now · " : ""}{sizeText(files[i].size)}</div>
                </div>
                <button type="button" onClick={() => removeFile(files[i].key)} aria-label={`Take ${s.title} out of the list`} data-testid={`myvideos-remove-${i}`}
                  className="p-2 rounded-full text-zinc-500 hover:text-white hover:bg-white/10"><X className="w-4 h-4" /></button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
